import {
  IconSettings,
  IconBrandAzure,
  IconBrandGithub,
  IconBox,
  IconBug,
} from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
} from "@tanstack/react-router";

import { cn } from "@/infrastructure/lib/utils";
import { useTRPC } from "@/infrastructure/trpc/react";
import { AppLayout, PageBreadcrumb } from "@/shared/layout";

// All valid settings routes
type SettingsRoute =
  | "/app/organization/$id/settings"
  | "/app/organization/$id/settings/azure-devops"
  | "/app/organization/$id/settings/azure-devops-mappings"
  | "/app/organization/$id/settings/github"
  | "/app/organization/$id/settings/github/issue-creation"
  | "/app/organization/$id/settings/github/bots"
  | "/app/organization/$id/settings/github/assignees"
  | "/app/organization/$id/settings/github/config"
  | "/app/organization/$id/settings/k8s-clusters"
  | "/app/organization/$id/settings/repo-mappings"
  | "/app/organization/$id/settings/sentry"
  | "/app/organization/$id/settings/sentry-mappings";

type SubNavItem = {
  to: SettingsRoute;
  label: string;
};

type NavItem = {
  to: SettingsRoute;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  subItems?: SubNavItem[];
};

// Navigation structure - integrations with nested mappings
const navigationItems: NavItem[] = [
  {
    to: "/app/organization/$id/settings",
    label: "General",
    icon: IconSettings,
    exact: true,
  },
  {
    to: "/app/organization/$id/settings/azure-devops",
    label: "Azure DevOps",
    icon: IconBrandAzure,
    subItems: [
      {
        to: "/app/organization/$id/settings/azure-devops-mappings",
        label: "Mappings",
      },
    ],
  },
  {
    to: "/app/organization/$id/settings/github",
    label: "GitHub",
    icon: IconBrandGithub,
    subItems: [
      {
        to: "/app/organization/$id/settings/github/issue-creation",
        label: "Issue Creation",
      },
      {
        to: "/app/organization/$id/settings/github/bots",
        label: "Bots",
      },
      {
        to: "/app/organization/$id/settings/github/assignees",
        label: "Assignees",
      },
      {
        to: "/app/organization/$id/settings/github/config",
        label: "Config",
      },
    ],
  },
  {
    to: "/app/organization/$id/settings/k8s-clusters",
    label: "Kubernetes",
    icon: IconBox,
    subItems: [
      {
        to: "/app/organization/$id/settings/repo-mappings",
        label: "Mappings",
      },
    ],
  },
  {
    to: "/app/organization/$id/settings/sentry",
    label: "Sentry",
    icon: IconBug,
    subItems: [
      {
        to: "/app/organization/$id/settings/sentry-mappings",
        label: "Mappings",
      },
    ],
  },
];

function SidebarNavLink({
  organizationId,
  to,
  label,
  icon: Icon,
  exact,
  isSubItem,
}: {
  organizationId: string;
  to: SettingsRoute;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  exact?: boolean | undefined;
  isSubItem?: boolean;
}) {
  return (
    <Link
      to={to}
      params={{ id: organizationId }}
      {...(exact && { activeOptions: { exact: true } })}
      className={cn(
        "flex items-center gap-2 rounded-md py-1.5 text-sm transition-colors",
        "text-muted-foreground hover:text-foreground",
        isSubItem ? "pr-2 pl-7" : "px-2"
      )}
      activeProps={{
        className: "text-foreground font-medium",
      }}
    >
      {Icon && <Icon className="size-4 shrink-0" />}
      <span>{label}</span>
    </Link>
  );
}

export const Route = createFileRoute(
  "/app/organization/$id/settings"
)({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient
        .query(
          context.trpc.organization.getById.queryOptions({
            id: params.id,
          })
        )
        .catch(() => undefined),
      context.queryClient
        .query(
          context.trpc.organizationMember.getMembersByOrganizationId.queryOptions(
            {
              organizationId: params.id,
            }
          )
        )
        .catch(() => undefined),
    ]);
  },
  component: RouteComponent,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();

  const { data } = useSuspenseQuery(
    trpc.organization.getById.queryOptions({ id })
  );
  const organization = data?.organization;

  const breadcrumbItems = [
    {
      label: organization?.name ?? "",
      to: "/app/organization/$id",
      params: { id },
    },
    {
      label: "Settings",
    },
  ];

  return (
    <AppLayout
      title={<PageBreadcrumb items={breadcrumbItems} />}
      description=""
      headerActions={null}
      fullWidth
    >
      <div className="flex gap-8">
        {/* Sidebar Navigation */}
        <aside className="w-40 shrink-0">
          <nav className="sticky top-6 space-y-0.5">
            {navigationItems.map((item) => (
              <div key={item.to}>
                <SidebarNavLink
                  organizationId={id}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  exact={item.exact}
                />
                {item.subItems && (
                  <div className="space-y-0.5">
                    {item.subItems.map((subItem) => (
                      <SidebarNavLink
                        key={subItem.to}
                        organizationId={id}
                        to={subItem.to}
                        label={subItem.label}
                        isSubItem
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </AppLayout>
  );
}
